/**
 * ReseauOnboardingWizard — Wizard de démarrage 4 étapes (a2)
 *
 * S'affiche automatiquement pour les pros dont l'onboarding réseau n'est ni
 * complet ni dismissé. Chaque étape est un mini-résumé et un CTA qui envoie
 * l'utilisateur sur l'écran d'édition correspondant. Le wizard n'écrit rien
 * lui-même — il s'appuie sur les écrans /profile, /reseau/ma-reputation…
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronRight, X, Sparkles, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const STEP_ORDER = ['identity', 'agency_zones', 'bio', 'first_proof'];

export default function ReseauOnboardingWizard({ enabled = true, onClose }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissing, setDismissing] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!enabled) { setLoading(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get('/reseau/onboarding/status');
                if (cancelled) return;
                setStatus(r.data);
                setOpen(Boolean(r.data?.show));
                const firstUndone = (r.data?.steps || []).findIndex((s) => !s.done);
                setActiveIdx(firstUndone >= 0 ? firstUndone : 0);
            } catch (e) {
                // Non bloquant
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [enabled]);

    const orderedSteps = useMemo(() => {
        if (!status?.steps) return [];
        const map = Object.fromEntries(status.steps.map((s) => [s.code, s]));
        return STEP_ORDER.map((c) => map[c]).filter(Boolean);
    }, [status]);

    if (loading || !open || !status?.show) return null;
    const step = orderedSteps[activeIdx] || orderedSteps[0];
    if (!step) return null;

    const handleAction = () => {
        navigate(step.cta_route);
        setOpen(false);
        if (onClose) onClose();
    };

    const handleDismiss = async () => {
        setDismissing(true);
        try { await api.post('/reseau/onboarding/dismiss', { reason: 'user_dismiss' }); }
        catch (e) { /* silent */ }
        setDismissing(false);
        setOpen(false);
        if (onClose) onClose();
    };

    return (
        <div
            data-testid="reseau-onboarding-wizard"
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            role="dialog"
            aria-labelledby="onboarding-title"
        >
            <div
                className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl"
                style={{ background: 'var(--reseau-surface)', color: 'var(--reseau-ink)' }}
            >
                <div className="flex items-start justify-between p-5 border-b"
                     style={{ borderColor: 'rgba(26,18,36,0.06)' }}>
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                             style={{ background: 'var(--reseau-violet-soft, rgba(104,23,147,0.1))',
                                      color: 'var(--reseau-violet)' }}>
                            <Sparkles className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider"
                               style={{ color: 'var(--reseau-violet)' }}>
                                Bienvenue sur Homly Réseau
                            </p>
                            <h2 id="onboarding-title" className="text-lg font-bold mt-0.5">
                                4 étapes pour être visible aux vendeurs
                            </h2>
                            <p className="text-xs mt-1" style={{ color: 'var(--reseau-ink-muted)' }}>
                                {status.done_count}/{status.total} déjà complétées · {status.percent}%
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleDismiss}
                        disabled={dismissing}
                        aria-label="Fermer le wizard d'onboarding"
                        data-testid="onboarding-dismiss-btn"
                        className="p-1.5 rounded-lg hover:bg-black/5"
                    >
                        {dismissing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                    </button>
                </div>

                <ol className="px-5 pt-4 space-y-2" data-testid="onboarding-steps">
                    {orderedSteps.map((s, i) => {
                        const isActive = i === activeIdx;
                        return (
                            <li key={s.code}>
                                <button
                                    type="button"
                                    onClick={() => setActiveIdx(i)}
                                    data-testid={`onboarding-step-${s.code}`}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition"
                                    style={{
                                        background: isActive ? 'rgba(104,23,147,0.06)' : 'transparent',
                                        border: isActive ? '1px solid rgba(104,23,147,0.25)' : '1px solid transparent',
                                    }}
                                >
                                    {s.done
                                        ? <CheckCircle2 className="w-4 h-4 flex-shrink-0"
                                                        style={{ color: '#16a34a' }} />
                                        : <Circle className="w-4 h-4 flex-shrink-0"
                                                  style={{ color: 'var(--reseau-ink-muted)' }} />}
                                    <span className="flex-1 text-sm font-medium">{s.title}</span>
                                    {isActive && (
                                        <ChevronRight className="w-4 h-4"
                                                      style={{ color: 'var(--reseau-violet)' }} />
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ol>

                <div className="px-5 py-4 mt-2 mx-5 mb-4 rounded-xl"
                     style={{ background: 'rgba(104,23,147,0.04)' }}
                     data-testid="onboarding-step-detail">
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1"
                       style={{ color: 'var(--reseau-violet)' }}>
                        Étape {activeIdx + 1}/{orderedSteps.length}
                    </p>
                    <h3 className="text-base font-bold mb-1">{step.title}</h3>
                    <p className="text-sm" style={{ color: 'var(--reseau-ink-muted)' }}>
                        {step.why}
                    </p>
                </div>

                <div className="px-5 pb-5 flex flex-col sm:flex-row gap-2 sm:justify-between">
                    <button
                        type="button"
                        onClick={handleDismiss}
                        disabled={dismissing}
                        data-testid="onboarding-skip-btn"
                        className="text-xs underline"
                        style={{ color: 'var(--reseau-ink-muted)' }}
                    >
                        Compléter plus tard
                    </button>
                    <button
                        type="button"
                        onClick={handleAction}
                        data-testid="onboarding-action-btn"
                        className="reseau-btn reseau-btn-primary px-4 py-2 rounded-lg text-sm font-semibold"
                        style={{ background: 'var(--reseau-violet)', color: '#fff' }}
                    >
                        {step.done ? 'Revoir cette étape' : step.cta_label || 'Compléter'}
                    </button>
                </div>
            </div>
        </div>
    );
}
